import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './EmployeeCard.module.scss';
import type { Employee } from '@/entities/employees';
import { formatDate } from '@/shared/utils';
import { StatusBadge } from '@/shared/components';
import { Tooltip } from '@/shared/ui';

export interface EmployeeCardProps {
  employee: Employee;
  isOnline?: boolean;
}

export const EmployeeCard: React.FC<EmployeeCardProps> = ({ employee, isOnline = false }) => {
  const { t } = useTranslation('employees');
  const fullName = `${employee.first_name} ${employee.last_name}`.trim() || employee.nickname;
  const initials =
    `${(employee.first_name || '').charAt(0)}${(employee.last_name || '').charAt(0)}`.toUpperCase() ||
    (employee.nickname || '?').slice(0, 2).toUpperCase();

  const [avatarLoaded, setAvatarLoaded] = useState(false);
  useEffect(() => {
    setAvatarLoaded(false);
  }, [employee.avatar_url]);

  return (
    <div className={styles['employee-card']}>
      <div className={styles['header']}>
        <div className={styles['header-main']}>
          {employee.avatar_url ? (
            <div className={styles['avatar-frame']}>
              {!avatarLoaded && <span className={styles['avatar-skeleton']} aria-hidden />}
              <img
                src={employee.avatar_url}
                alt=""
                className={classNames(
                  styles['avatar-image'],
                  avatarLoaded && styles['avatar-image--visible']
                )}
                onLoad={() => setAvatarLoaded(true)}
                onError={() => setAvatarLoaded(true)}
                decoding="async"
              />
            </div>
          ) : (
            <div className={styles['avatar']} aria-hidden>
              {initials}
            </div>
          )}
          <div className={styles['header-left']}>
            <div className={styles['nameRow']}>
              <h3 className={styles['name']}>{fullName}</h3>
              {isOnline && (
                <Tooltip content={t('employeeCard.online')}>
                  <span
                    className={styles['onlineDot']}
                    aria-label={t('employeeCard.online')}
                  />
                </Tooltip>
              )}
            </div>
            <div className={styles['meta']}>
              <span className={styles['email']}>{employee.email}</span>
              {employee.nickname && employee.nickname !== fullName && (
                <span className={styles['nickname']}>@{employee.nickname}</span>
              )}
            </div>
          </div>
        </div>
        <div className={styles['header-right']}>
          <StatusBadge
            status={employee.blocked ? 'negative' : 'positive'}
            text={employee.blocked ? t('employeeCard.blocked') : t('employeeCard.active')}
          />
          <StatusBadge
            status={employee.bypass_2fa ? 'caution' : 'positive'}
            text={employee.bypass_2fa ? t('employeeCard.twoFactorDisabled') : t('employeeCard.twoFactorEnabled')}
          />
        </div>
      </div>
      
      <div className={styles['content']}>
        <div className={styles['info-row']}>
          <span className={styles['label']}>{t('employeeCard.role')}</span>
          <span className={styles['value']}>{employee.role || '-'}</span>
        </div>
        
        {employee.phone && (
          <div className={styles['info-row']}>
            <span className={styles['label']}>{t('employeeCard.phone')}</span>
            <span className={styles['value']}>{employee.phone}</span>
          </div>
        )}
        
        <div className={styles['info-row']}>
          <span className={styles['label']}>{t('employeeCard.registrationDate')}</span>
          <span className={styles['value']}>
            {employee.registration_date ? formatDate(employee.registration_date, true) : '-'}
          </span>
        </div>
        
        {employee.permissions && employee.permissions.length > 0 && (
          <div className={styles['info-row']}>
            <span className={styles['label']}>{t('employeeCard.permissions')}</span>
            <span className={styles['value']}>
              {employee.permissions.length} {employee.permissions.length === 1 ? t('employeeCard.permission') : t('employeeCard.permissionsDeclension')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
